import { useNavigate } from "react-router-dom";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { useI18n } from "../i18n/I18nProvider";

/**
 * Standard page header used across modules.
 *
 * <PageHeader
 *   title="Risk Register"
 *   description="Track risks across assets and frameworks."
 *   breadcrumb={[{ label: "Compliance", path: "/compliance" }, { label: "Risks" }]}
 *   back="/dashboard"
 *   actions={<Button>+ New risk</Button>}
 * />
 */
export function PageHeader({
  title,
  description,
  breadcrumb,
  back,
  actions,
  className,
  children,
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <div className={cn("space-y-3", className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {breadcrumb.map((item, i) => {
              const isLast = i === breadcrumb.length - 1;
              return (
                <li key={i} className="flex items-center gap-1.5">
                  {item.path && !isLast ? (
                    <button
                      type="button"
                      onClick={() => navigate(item.path)}
                      className="hover:text-foreground focus-visible:text-foreground focus-visible:outline-none rounded"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className={isLast ? "text-foreground font-medium" : ""} aria-current={isLast ? "page" : undefined}>
                      {item.label}
                    </span>
                  )}
                  {!isLast && <ChevronRight className="w-3 h-3" aria-hidden="true" />}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1 min-w-0">
          {back && (
            <button
              type="button"
              onClick={() => navigate(back)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <ArrowLeft className="w-3 h-3" aria-hidden="true" />
              <span>{t("back")}</span>
            </button>
          )}
          <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight truncate">
            {title}
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {children}
    </div>
  );
}
